import { useState, useEffect } from 'react';
import axios from '../../axios/axiosconf';
import Header from '../general/Header';
import Aside from '../general/Aside';
import Footer from '../general/Footer';
import Unauthorized from '../general/Unauthorized';
import Loading from '../general/Loading';
import { Link } from 'react-router-dom';
import { formatDateOnly } from '../superadmin/superadminDateUtils';
import PaginationBar from '../general/PaginationBar';

const FORMA_PAGO_OPCIONES = [
    { value: '10', label: 'Efectivo' },
    { value: '42', label: 'Consignación bancaria' },
    { value: '1', label: 'Instrumento no definido' },
    { value: '20', label: 'Cheque' },
    { value: '30', label: 'Transferencia Crédito' },
    { value: '31', label: 'Transferencia Débito' },
    { value: '45', label: 'Transferencia Crédito Bancario' },
    { value: '47', label: 'Transferencia Débito Bancaria' },
    { value: '48', label: 'Tarjeta Crédito' },
    { value: '49', label: 'Tarjeta Débito' },
    { value: 'ZZZ', label: 'Acuerdo mutuo' }
];

function LibroDiario() {
    // Estados de autenticación y permisos
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(null);
    const [client, setClient] = useState({});

    // Estados de datos
    const [movimientos, setMovimientos] = useState([]);
    const [totales, setTotales] = useState({
        total_ingresos: 0,
        total_egresos: 0,
        balance: 0,
        total_caja_efectivo: 0
    });

    // Estados de filtros
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [usuarioFiltro, setUsuarioFiltro] = useState('');
    const [formaPagoFiltro, setFormaPagoFiltro] = useState('');
    const [usuarios, setUsuarios] = useState([]);

    // Estados de paginación
    const [paginaActual, setPaginaActual] = useState(1);
    const [totalPaginas, setTotalPaginas] = useState(1);
    const [totalRegistros, setTotalRegistros] = useState(0);

    // Estado de carga
    const [loading, setLoading] = useState(false);

    // Fecha local YYYY-MM-DD (evita desfase por UTC con toISOString)
    const getFechaActual = () => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    useEffect(() => {
        let userdata = {};

        if (localStorage.getItem('userdata')) {
            userdata = JSON.parse(localStorage.getItem('userdata'));
        }

        // Validación de permisos híbrida (nuevo + legacy)
        let tienePermiso = false;

        if (userdata.permissions && Array.isArray(userdata.permissions)) {
            tienePermiso = userdata.permissions.some(permission =>
                permission.slug === 'ver_reporte_libro_diario'
            );
        } else if (userdata.up && userdata.up.includes('ver_reporte_libro_diario')) {
            tienePermiso = true;
        }

        setHasPermission(tienePermiso);

        if (!tienePermiso) {
            return;
        }

        // Cargar cliente
        axios.get('/get-client?uid=' + userdata.user_id)
            .then(response => {
                if (response.status === 200) {
                    setClient(response.data);
                    setLoggedIn(true);
                }
            })
            .catch(error => {
                console.error('Error al cargar cliente:', error);
            });

        // Cargar usuarios para el filtro
        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then(response => {
                if (response.status === 200) {
                    setUsuarios(response.data);
                }
            })
            .catch(error => {
                console.error('Error al cargar usuarios:', error);
            });

        // Fechas por defecto: mismo día (hoy)
        const hoy = getFechaActual();
        setFechaInicio(hoy);
        setFechaFin(hoy);

    }, []);

    // Cargar movimientos cuando cambian los filtros o la página
    useEffect(() => {
        if (loggedIn && hasPermission && fechaInicio && fechaFin) {
            cargarMovimientos();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedIn, hasPermission, paginaActual]);

    useEffect(() => {
        if (totalPaginas > 0 && paginaActual > totalPaginas) {
            setPaginaActual(totalPaginas);
        }
    }, [paginaActual, totalPaginas]);

    const cargarMovimientos = () => {
        setLoading(true);

        let userdata = {};
        if (localStorage.getItem('userdata')) {
            userdata = JSON.parse(localStorage.getItem('userdata'));
        }

        const params = {
            uid: userdata.user_id,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            page: paginaActual,
            per_page: 12
        };

        if (usuarioFiltro) {
            params.usuario_id = usuarioFiltro;
        }
        if (formaPagoFiltro) {
            params.forma_pago = formaPagoFiltro;
        }

        axios.get('/reportes/libro-diario', { params })
            .then(response => {
                if (response.status === 200) {
                    setMovimientos(response.data.movimientos || []);
                    setTotales(response.data.totales || {
                        total_ingresos: 0,
                        total_egresos: 0,
                        balance: 0,
                        total_caja_efectivo: 0
                    });
                    setTotalPaginas(response.data.total_paginas || 1);
                    setTotalRegistros(response.data.total_registros || 0);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Error al cargar movimientos:', error);
                setLoading(false);
            });
    };

    const handleBuscar = (e) => {
        e.preventDefault();
        setPaginaActual(1);
        cargarMovimientos();
    };

    const handleLimpiar = () => {
        const hoy = getFechaActual();
        setFechaInicio(hoy);
        setFechaFin(hoy);
        setUsuarioFiltro('');
        setFormaPagoFiltro('');
        setPaginaActual(1);
    };

    const handleExportarExcel = () => {
        setLoading(true);

        let userdata = {};
        if (localStorage.getItem('userdata')) {
            userdata = JSON.parse(localStorage.getItem('userdata'));
        }

        const params = {
            uid: userdata.user_id,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin
        };

        if (usuarioFiltro) {
            params.usuario_id = usuarioFiltro;
        }
        if (formaPagoFiltro) {
            params.forma_pago = formaPagoFiltro;
        }

        // Crear URL con parámetros
        const queryString = new URLSearchParams(params).toString();
        const url = `/reportes/libro-diario/exportar-excel?${queryString}`;

        // Descargar archivo
        axios.get(url, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${userdata.token || localStorage.getItem('token')}`
            }
        })
            .then(response => {
                // Crear un enlace temporal para descargar el archivo
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `libro_diario_${new Date().getTime()}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                setLoading(false);
            })
            .catch(error => {
                console.error('Error al exportar:', error);
                alert('Error al exportar el reporte a Excel');
                setLoading(false);
            });
    };

    const formatearMoneda = (valor) => {
        return '$' + Number(valor).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const formatearFecha = (fecha) => formatDateOnly(fecha);

    const formatearNumero = (numero) => {
        if (!numero) return '';
        let n = String(numero).trim();
        if (n.startsWith('-')) n = n.slice(1);
        return n.replace(/^([A-Za-z]+)-(.+)$/, '$1$2');
    };

    const obtenerCliente = (mov) => {
        if (mov.cliente) return mov.cliente.trim();
        if (!mov.descripcion) return '';
        const partes = mov.descripcion.split(' - ');
        return partes.length >= 2 ? partes[partes.length - 1].trim() : mov.descripcion;
    };

    const getFormaPagoLabel = (codigo) => {
        if (!codigo) return '-';
        const op = FORMA_PAGO_OPCIONES.find(o => o.value === String(codigo));
        return op ? op.label : codigo;
    };

    const getBadgeClass = (tipo) => {
        switch (tipo) {
            case 'Factura':
                return 'report-chip report-chip--factura';
            case 'Recibo':
                return 'report-chip report-chip--recibo';
            case 'Egreso':
                return 'report-chip report-chip--egreso';
            default:
                return 'report-chip report-chip--default';
        }
    };

    const totalCajaEfectivo = Number(totales.total_caja_efectivo || 0);

    // Protección de ruta
    if (hasPermission === false) {
        return <Unauthorized />;
    }

    if (!loggedIn || hasPermission === null) {
        return <Loading />;
    }

    return (
        <div className="hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">Libro Diario</li>
                                </ol>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <h1 className="m-0">
                                    <i className="fas fa-book mr-2"></i>
                                    Libro Diario
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">

                        {/* Filtros */}
                        <div className="card report-fin-card">
                            <div className="card-header">
                                <h3 className="card-title">
                                    <i className="fas fa-filter mr-2"></i>
                                    Filtros
                                </h3>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleBuscar}>
                                    <div className="row">
                                        <div className="col-md-2 col-lg-2">
                                            <div className="form-group">
                                                <label>Fecha Inicio</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={fechaInicio}
                                                    onChange={(e) => setFechaInicio(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-2 col-lg-2">
                                            <div className="form-group">
                                                <label>Fecha Fin</label>
                                                <input
                                                    type="date"
                                                    className="form-control form-control-sm"
                                                    value={fechaFin}
                                                    onChange={(e) => setFechaFin(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-lg-3">
                                            <div className="form-group">
                                                <label>Profesional (Opcional)</label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={usuarioFiltro}
                                                    onChange={(e) => setUsuarioFiltro(e.target.value)}
                                                >
                                                    <option value="">Todos</option>
                                                    {usuarios.map(usuario => (
                                                        <option key={usuario.id ?? usuario.user_id} value={usuario.id ?? usuario.user_id}>
                                                            {(usuario.name || usuario.user_name || '').trim() || `Usuario ${usuario.id ?? usuario.user_id}`}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-lg-3">
                                            <div className="form-group">
                                                <label>Forma de Pago (Opcional)</label>
                                                <select
                                                    className="form-control form-control-sm"
                                                    value={formaPagoFiltro}
                                                    onChange={(e) => setFormaPagoFiltro(e.target.value)}
                                                >
                                                    <option value="">Todas</option>
                                                    {FORMA_PAGO_OPCIONES.map(op => (
                                                        <option key={op.value} value={op.value}>{op.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-2 col-lg-2">
                                            <div className="form-group mb-md-0">
                                                <label>&nbsp;</label>
                                                <div className="d-flex flex-nowrap align-items-center" style={{ gap: '6px' }}>
                                                    <button type="submit" className="btn btn-primary btn-sm px-2" disabled={loading} title="Buscar">
                                                        <i className="fas fa-search"></i>
                                                    </button>
                                                    <button type="button" className="btn btn-secondary btn-sm px-2" onClick={handleLimpiar} disabled={loading} title="Limpiar">
                                                        <i className="fas fa-eraser"></i>
                                                    </button>
                                                    <Link to="/estadisticas-contables" className="btn btn-outline-secondary btn-sm px-2" title="Volver">
                                                        <i className="fas fa-arrow-left"></i>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="report-fin-hint mt-3 mb-0">
                                        Ingresos: facturas de contado emitidas (estados contables) y recibos de caja ligados a facturas a crédito emitidas.
                                        Egresos: movimientos del período. No se incluyen borradores ni facturas sin efecto contable en caja.
                                    </p>
                                </form>
                            </div>
                        </div>

                        {/* Cards de Totales */}
                        <div className="row">
                            <div className="col-md-3">
                                <div className="card card-outline card-success">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h3 className="mb-0">{formatearMoneda(totales.total_ingresos)}</h3>
                                                <p className="text-muted mb-0">Total Ingresos</p>
                                            </div>
                                            <div className="report-fin-kpi-icon" style={{ background: 'rgba(40, 167, 69, 0.10)', color: '#28a745' }}>
                                                <i className="fas fa-arrow-up"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-danger">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h3 className="mb-0">{formatearMoneda(totales.total_egresos)}</h3>
                                                <p className="text-muted mb-0">Total Egresos</p>
                                            </div>
                                            <div className="report-fin-kpi-icon" style={{ background: 'rgba(220, 53, 69, 0.10)', color: '#dc3545' }}>
                                                <i className="fas fa-arrow-down"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-secondary">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h3 className="mb-0">{formatearMoneda(totales.balance)}</h3>
                                                <p className="text-muted mb-0">Balance</p>
                                            </div>
                                            <div className="report-fin-kpi-icon" style={{ background: 'rgba(108, 117, 125, 0.12)', color: '#6c757d' }}>
                                                <i className="fas fa-balance-scale"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-warning">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h3 className="mb-0">{formatearMoneda(totalCajaEfectivo)}</h3>
                                                <p className="text-muted mb-0">Total en Caja (Efectivo)</p>
                                            </div>
                                            <div className="report-fin-kpi-icon" style={{ background: 'rgba(255, 193, 7, 0.12)', color: '#d39e00' }}>
                                                <i className="fas fa-cash-register"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tabla de Movimientos */}
                        <div className="card report-fin-card">
                            <div className="card-header">
                                <h3 className="card-title">
                                    <i className="fas fa-list mr-2"></i>
                                    Movimientos ({totalRegistros})
                                </h3>
                                <div className="card-tools">
                                    <button
                                        className="btn btn-outline-success btn-sm"
                                        onClick={handleExportarExcel}
                                        disabled={loading || movimientos.length === 0}
                                    >
                                        <i className="fas fa-file-excel mr-1"></i>
                                        Exportar Excel
                                    </button>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <i className="fas fa-spinner fa-spin fa-3x text-primary"></i>
                                        <p className="mt-3 text-muted small">Cargando movimientos...</p>
                                    </div>
                                ) : (
                                    <div className="report-fin-table-wrap report-fin-table-wrap--fit">
                                        <table className="table table-hover mb-0 report-fin-table">
                                            <thead>
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Tipo</th>
                                                    <th>Número</th>
                                                    <th>Cliente</th>
                                                    <th>Usuario</th>
                                                    <th>Forma de Pago</th>
                                                    <th className="text-right">Ingresos</th>
                                                    <th className="text-right">Egresos</th>
                                                    <th className="text-right">Balance</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {movimientos.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-4">
                                                            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                                            <p className="text-muted">No se encontraron movimientos para el período seleccionado</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    movimientos.map((mov, index) => (
                                                        <tr key={`${mov.tipo}-${mov.numero}-${index}`}>
                                                            <td>{formatearFecha(mov.fecha)}</td>
                                                            <td>
                                                                <span className={getBadgeClass(mov.tipo)}>
                                                                    {mov.tipo}
                                                                </span>
                                                            </td>
                                                            <td>{formatearNumero(mov.numero)}</td>
                                                            <td>{obtenerCliente(mov)}</td>
                                                            <td>{mov.usuario}</td>
                                                            <td>{getFormaPagoLabel(mov.forma_pago)}</td>
                                                            <td className="text-right text-success font-weight-bold">
                                                                {mov.ingreso > 0 ? formatearMoneda(mov.ingreso) : '-'}
                                                            </td>
                                                            <td className="text-right text-danger font-weight-bold">
                                                                {mov.egreso > 0 ? formatearMoneda(mov.egreso) : '-'}
                                                            </td>
                                                            <td className="text-right font-weight-bold">
                                                                {formatearMoneda(mov.balance_acumulado)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {movimientos.length > 0 && (
                                                <tfoot className="bg-light">
                                                    <tr>
                                                        <td colSpan="6" className="text-right font-weight-bold">TOTALES:</td>
                                                        <td className="text-right text-success font-weight-bold">
                                                            {formatearMoneda(totales.total_ingresos)}
                                                        </td>
                                                        <td className="text-right text-danger font-weight-bold">
                                                            {formatearMoneda(totales.total_egresos)}
                                                        </td>
                                                        <td
                                                            className="text-right font-weight-bold"
                                                            style={{ color: totales.balance >= 0 ? '#28a745' : '#dc3545' }}
                                                        >
                                                            {formatearMoneda(totales.balance)}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                )}
                            </div>
                            {totalPaginas > 1 && (
                                <div className="card-footer clearfix bg-white border-top py-2">
                                    <div className="float-right">
                                        <PaginationBar
                                            paginaActual={paginaActual}
                                            totalPaginas={totalPaginas}
                                            onPageChange={setPaginaActual}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </section>
            </div>

            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default LibroDiario;
